import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../../components/main-heading.component";
import { _get, _post } from "../../../services/api.service";
import "../content-engine.css";
import { contentEngineService } from "../content-engine.service";
import { getContentFormatIcon } from "../../../utils/contentFormatIcons";
import "../../video-create/CreateVideo.css";
import "./SelectContentFormat.css";

interface ContentFormat {
  id: number;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  best_for: string[];
  example_title: string;
  preview_type: string;
  preview_config: Record<string, unknown>;
  icon: string;
  is_active: boolean;
}

interface FormatRanking {
  format_id: number;
  score: number;
  reason: string;
}

export function FormatPreview({ format }: { format: ContentFormat }) {
  switch (format.preview_type) {
    case "numbered_list": {
      const examples = format.preview_config?.examples as string[] | undefined;
      const count = (format.preview_config?.count as number) || 5;
      const items = examples || Array.from({ length: count }, () => "" as string);
      return (
        <div className="preview-box">
          {items.map((item: string, i: number) => (
            <div key={i} className="preview-row">
              <span className="preview-num">{i + 1}.</span>
              <span className="preview-text">{item || ""}</span>
            </div>
          ))}
        </div>
      );
    }
    case "comparison":
      return (
        <div className="preview-box preview-compare">
          <div className="preview-col">
            <div className="preview-label">Option A</div>
            <div className="preview-line short" />
            <div className="preview-line short" />
          </div>
          <div className="preview-vs">VS</div>
          <div className="preview-col">
            <div className="preview-label">Option B</div>
            <div className="preview-line short" />
            <div className="preview-line short" />
          </div>
        </div>
      );
    case "tutorial":
      return (
        <div className="preview-box preview-steps">
          <div className="preview-step">1</div>
          <div className="preview-arrow">→</div>
          <div className="preview-step">2</div>
          <div className="preview-arrow">→</div>
          <div className="preview-step">3</div>
        </div>
      );
    case "timeline":
      return (
        <div className="preview-box preview-timeline">
          <div className="preview-tl-item">
            <div className="preview-tl-dot" />
            <div className="preview-tl-line" />
          </div>
          <div className="preview-tl-item">
            <div className="preview-tl-dot" />
            <div className="preview-tl-line" />
          </div>
          <div className="preview-tl-item">
            <div className="preview-tl-dot" />
          </div>
        </div>
      );
    case "story":
      return (
        <div className="preview-box preview-story">
          <div className="preview-story-arc" />
          <div className="preview-story-label">Beginning → Middle → End</div>
        </div>
      );
    case "myth_fact":
      return (
        <div className="preview-box preview-mythfact">
          <div className="preview-myth">❌ Common Myth</div>
          <div className="preview-arrow">↓</div>
          <div className="preview-fact">✅ Verified Fact</div>
        </div>
      );
    case "before_after":
      return (
        <div className="preview-box preview-ba">
          <div className="preview-ba-item">
            <div className="preview-ba-label">Before</div>
            <div className="preview-line short" />
          </div>
          <div className="preview-arrow">→</div>
          <div className="preview-ba-item">
            <div className="preview-ba-label">After</div>
            <div className="preview-line short" />
          </div>
        </div>
      );
    case "quote":
      return (
        <div className="preview-box preview-quote">
          <div className="preview-quote-text">" "</div>
        </div>
      );
    default:
      return (
        <div className="preview-box">
          <div className="preview-line" />
          <div className="preview-line short" />
        </div>
      );
  }
}

const SelectContentFormat: React.FC = () => {
  const navigate = useNavigate();
  const steps = [
    { label: "Topic", description: "Enter your topic idea", path: "/content-engine" },
    { label: "Format", description: "Choose your content format", path: "/content-engine/select-format" },
    { label: "Scripts", description: "Pick from generated scripts", path: "/content-engine/choose-script" },
    { label: "Refinement", description: "Refine and finalize your script", path: "/content-engine/script-detail" },
  ];
  const currentStep = 1;
  const [topic, setTopic] = useState("");
  const [formats, setFormats] = useState<ContentFormat[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [recommendations, setRecommendations] = useState<FormatRanking[]>([]);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches,
  );
  const fetched = useRef(false);
  const badgesShownRef = useRef(localStorage.getItem("fmt_badges") === "1");

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 700px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);

  const fetchRecommendations = async (topicText: string, currentFormats: ContentFormat[]) => {
    try {
      const result = await _post("/content-engine/content-formats/recommend/", { topic: topicText }, {}, false, true);
      const ranks: FormatRanking[] = result?.rankings || [];
      setRecommendations(ranks);
      localStorage.setItem("fmt_badges", "1");
      if (ranks.length > 0 && !selectedId) {
        const best = currentFormats.find((f) => f.id === ranks[0].format_id);
        if (best) setSelectedId(best.id);
      }
    } catch (error) {
      console.error("Recommendation failed", error);
    }
  };

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const selectedTopic = contentEngineService.getSelectedTopic();
        if (!selectedTopic) {
          navigate("/content-engine", { replace: true });
          return;
        }
        setTopic(selectedTopic.name);
        const response: ContentFormat[] = (await _get("/content-engine/content-formats/", {}, true)) || [];
        setFormats(response);
        fetchRecommendations(selectedTopic.name, response);
      } catch (error) {
        console.error("Error fetching content formats", error);
      }
    })();
  }, []);

  const selectedFormat = formats.find((f) => f.id === selectedId) || null;

  const handleFormatSelect = (format: ContentFormat) => {
    if (!format.is_active) return;
    setSelectedId(format.id);
  };

  const handleContinue = () => {
    if (!selectedFormat) return;
    const selectedTopic = contentEngineService.getSelectedTopic();
    if (!selectedTopic) return;
    contentEngineService.setSelectedContentFormat(selectedFormat.id, selectedFormat.name);
    navigate("/content-engine/choose-script");
  };

  return (
    <div className="container-fluid select-format-page">
      <MainHeading
        title={topic ? `${topic.charAt(0).toUpperCase()}${topic.slice(1).toLowerCase()}` : "Select Format"}
        subTitle="Choose the content format that fits your topic best"
      />
      <div className="cv-workspace">
        <aside className="cv-progress-panel" aria-label="Content engine progress">
          <div className="cv-progress-caption">CONTENT ENGINE</div>
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = index === currentStep;
            return (
              <div
                key={step.label}
                className={`cv-progress-step${complete ? " complete" : ""}${active ? " active" : ""}${complete ? " clickable" : ""}`}
                onClick={() => complete && navigate(step.path)}
                onKeyDown={(e) => complete && e.key === "Enter" && navigate(step.path)}
                role={complete ? "button" : undefined}
                tabIndex={complete ? 0 : undefined}
              >
                <div className="cv-progress-rail">
                  <span>{complete ? "✓" : index + 1}</span>
                </div>
                <div>
                  <div className="cv-progress-title">{step.label}</div>
                  <div className="cv-progress-description">{step.description}</div>
                </div>
              </div>
            );
          })}
        </aside>

        <button
          type="button"
          className="ti-back-link ce-mobile-nav-link"
          onClick={() => navigate("/content-engine")}
        >
          ← Back to Topics
        </button>

        <section className="cv-creator-card">
          <div className="cv-card-inner">
            <div className="format-top">
              <p className="subtitle">What content type do you want to create?</p>
              <button
                type="button"
                className="ti-back-link ce-desktop-nav-link"
                onClick={() => navigate("/content-engine")}
              >
                ← Back to Topics
              </button>
            </div>

            <div className="format-grid-xl">
              {formats.length === 0 ? (
                <div className="hint">No content formats available</div>
              ) : (
                formats.map((format) => {
                  const isSelected = selectedId === format.id;
                  const rankIndex = recommendations.findIndex((r) => r.format_id === format.id);
                  const isBestMatch = rankIndex === 0;
                  const isTopThree = rankIndex >= 0 && rankIndex < 3;
                  return (
                    <div
                      key={format.id}
                      className={`format-card${isSelected ? " selected" : ""}${!format.is_active ? " disabled" : ""}`}
                      onClick={() => handleFormatSelect(format)}
                    >
                      <div className="format-badges">
                        {!format.is_active && <span className="badge coming-badge">Coming soon</span>}
                        {badgesShownRef.current && format.is_active && isBestMatch && (
                          <span className="badge best-badge">⭐ Best Match</span>
                        )}
                        {badgesShownRef.current && format.is_active && isTopThree && rankIndex === 1 && (
                          <span className="badge pick-badge">👍 Good alternative</span>
                        )}
                        {badgesShownRef.current && format.is_active && isTopThree && rankIndex === 2 && (
                          <span className="badge decent-badge">👍 Decent fit</span>
                        )}
                      </div>
                      <div className="format-card-header">
                        <span className="format-icon">
                          {React.createElement(getContentFormatIcon(format.slug), {
                            sx: { fontSize: 20, verticalAlign: "middle" },
                          })}
                        </span>
                        <span className="format-card-name">{format.name}</span>
                        {format.preview_config?.blocks ? (
                          <span className="format-blocks">~{String(format.preview_config.blocks)} blocks</span>
                        ) : null}
                        {isSelected && <span className="format-check">✓</span>}
                      </div>
                      <div className="format-card-desc">
                        {format.short_description || format.description}
                      </div>
                      {format.best_for && format.best_for.length > 0 && (
                        <div className="format-best-for">
                          <span className="format-best-label">PERFECT FOR</span>
                          <div className="format-best-tags">
                            {format.best_for.map((tag) => (
                              <span key={tag} className="format-tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {format.example_title && (
                        <div className="format-example">
                          <span className="format-example-label">Example</span>
                          <span className="format-example-title">"{format.example_title}"</span>
                        </div>
                      )}
                      <details className="format-card-preview" open={isMobile ? undefined : true}>
                        <summary
                          className="format-card-preview-toggle"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          Preview layout
                        </summary>
                        <div className="format-card-bottom">
                          <FormatPreview format={format} />
                        </div>
                      </details>
                    </div>
                  );
                })
              )}
            </div>

            <div className="cv-actions ce-format-actions">
              <button
                className="cv-btn cv-btn-primary"
                disabled={!selectedFormat}
                onClick={handleContinue}
              >
                Continue <span className="btn-arrow">→</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SelectContentFormat;
